import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "@env";
import type {
  Challenge,
  ChallengeAddOn,
  ChallengeDefinition,
  ChallengeOrderStatus,
  ChallengeTradesPaginated,
  GroupedChallenges,
  OrdersPaginated,
  PaginationPayload,
  Payout,
  PaymentData,
  PayoutMethod,
  ProcessedPayoutsPaginated,
  UpgradePaymentData,
  UserChallenge,
} from "@models";
import { ContractsPaginated } from "../../shared/model/contracts.model";
import { CookieService } from "./cookie.service";
import type { LegacyResponse, PSPPaymentResponse } from "./response.model";
import { Observable } from "rxjs";
// STUB(instant-accounts) START
import {
  getInstantStubChallengeResponse$,
  getInstantStubTrades$,
  isInstantStubId,
} from "../../shared/stubs/instant-accounts.stub";
// STUB(instant-accounts) END
// STUB(revamp-accounts) START
import {
  getRevampStubChallengeResponse$,
  getRevampStubTrades$,
  isRevampStubId,
} from "../../shared/stubs/revamp-accounts.stub";
// STUB(revamp-accounts) END
// STUB(provisioning-accounts) START
import {
  getProvisioningStubChallengeError$,
  getProvisioningStubTrades$,
  isProvisioningStubId,
  PROVISIONING_STUB_ENABLED,
  withProvisioningStub$,
  withProvisioningStubLegacy,
} from "../../shared/stubs/provisioning-accounts.stub";
// STUB(provisioning-accounts) END

@Injectable({ providedIn: "root" })
export class ChallengeService {
  private _challengesApiUrl: string = environment.newChallengesApiUrl;
  private _purchasesApiUrl: string = environment.purchasesApiUrl;
  private _contractsApiUrl: string = environment.contractsApiUrl;
  private _payoutsApiUrl: string = environment.payoutsApiUrl;
  private _statisticsApiUrl: string = environment.statisticsApiUrl;

  constructor(
    private _httpClient: HttpClient,
    private _cookiesService: CookieService,
  ) {}

  public getChallengeDefinitions(): Observable<LegacyResponse<ChallengeDefinition[]>> {
    return this._httpClient.get<LegacyResponse<ChallengeDefinition[]>>(
      `${this._purchasesApiUrl}/definitions`,
    );
  }

  public getChallengeById(challengeId: string): Observable<LegacyResponse<Challenge>> {
    // STUB(instant-accounts) START: fake details for the two stub Instant accounts
    if (isInstantStubId(challengeId)) {
      return getInstantStubChallengeResponse$(challengeId);
    }
    // STUB(instant-accounts) END
    // STUB(revamp-accounts) START: fake details for the revamp scenario accounts (one per detail-page case)
    if (isRevampStubId(challengeId)) {
      return getRevampStubChallengeResponse$(challengeId);
    }
    // STUB(revamp-accounts) END
    // STUB(provisioning-accounts) START: a stub placeholder is not a challenge yet, 404 like the backend
    if (isProvisioningStubId(challengeId)) {
      return getProvisioningStubChallengeError$(challengeId);
    }
    // STUB(provisioning-accounts) END
    return this._httpClient.get<LegacyResponse<Challenge>>(
      `${this._challengesApiUrl}/${challengeId}`,
    );
  }

  public redeemFreeChallenge(
    voucherId: string,
    challengeDefinitionId: string,
  ): Observable<UserChallenge> {
    return this._httpClient.post<UserChallenge>(`${this._purchasesApiUrl}/redeem`, {
      voucherId,
      challengeDefinitionId,
    });
  }

  public setChallengeStatus(
    challengeId: string,
    status: "VISIBLE" | "HIDDEN",
  ): Observable<Challenge> {
    return this._httpClient.put<Challenge>(
      `${this._challengesApiUrl}/${challengeId}/${status === "VISIBLE" ? "show" : "hide"}`,
      null,
    );
  }

  public getUserChallenges(): Observable<LegacyResponse<GroupedChallenges>> {
    const response$: Observable<LegacyResponse<GroupedChallenges>> = this._httpClient.get<
      LegacyResponse<GroupedChallenges>
    >(`${this._challengesApiUrl}/mine`);
    // STUB(provisioning-accounts) START: adds the 4 stub placeholders to the provisioning group
    if (PROVISIONING_STUB_ENABLED) {
      return withProvisioningStubLegacy(response$);
    }
    // STUB(provisioning-accounts) END
    return response$;
  }

  public getUserActiveChallenges(): Observable<GroupedChallenges> {
    const response$: Observable<GroupedChallenges> = this._httpClient.get<GroupedChallenges>(
      `${this._challengesApiUrl}/mine/active`,
    );
    // STUB(provisioning-accounts) START: adds the 4 stub placeholders to the provisioning group
    if (PROVISIONING_STUB_ENABLED) {
      return withProvisioningStub$(response$);
    }
    // STUB(provisioning-accounts) END
    return response$;
  }

  public getChallengeTradeHistory(
    challengeId: string,
    paginationPayload: PaginationPayload,
  ): Observable<ChallengeTradesPaginated> {
    // STUB(instant-accounts) START: fake trades for the two stub Instant accounts
    if (isInstantStubId(challengeId)) {
      return getInstantStubTrades$(paginationPayload);
    }
    // STUB(instant-accounts) END
    // STUB(revamp-accounts) START: fake trades for the revamp scenario accounts
    if (isRevampStubId(challengeId)) {
      return getRevampStubTrades$(paginationPayload);
    }
    // STUB(revamp-accounts) END
    // STUB(provisioning-accounts) START: no trades for the PROVISIONING stub accounts (the page redirects)
    if (isProvisioningStubId(challengeId)) {
      return getProvisioningStubTrades$(paginationPayload);
    }
    // STUB(provisioning-accounts) END
    const { page, size } = paginationPayload;

    return this._httpClient.get<ChallengeTradesPaginated>(
      `${this._statisticsApiUrl}/trades/history/${challengeId}`,
      { params: { page, size } },
    );
  }

  public getTradeByTradeId(tradeId: string): Observable<ChallengeTradesPaginated> {
    return this._httpClient.get<ChallengeTradesPaginated>(
      `${this._statisticsApiUrl}/trades/${tradeId}`,
    );
  }

  public getUserOpenPayouts(): Observable<{ openPayouts: Payout[] }> {
    return this._httpClient.get<{ openPayouts: Payout[] }>(`${this._payoutsApiUrl}/mine`);
  }

  public checkPaymentStatus(orderId: string): Observable<LegacyResponse<ChallengeOrderStatus>> {
    return this._httpClient.get<LegacyResponse<ChallengeOrderStatus>>(
      `${this._purchasesApiUrl}/orders/${orderId}`,
    );
  }

  public initializePayment(data: PaymentData): Observable<LegacyResponse<PSPPaymentResponse>> {
    let headers = new HttpHeaders();
    headers = headers.set("FBC", this._cookiesService.get("_fbc") || "");
    headers = headers.set("FBP", this._cookiesService.get("_fbp") || "");

    return this._httpClient.post<LegacyResponse<PSPPaymentResponse>>(
      `${this._purchasesApiUrl}/buy-challenge`,
      data,
      { headers },
    );
  }

  public initializeUpgradePayment(
    data: UpgradePaymentData,
  ): Observable<LegacyResponse<PSPPaymentResponse>> {
    let headers: HttpHeaders = new HttpHeaders();
    headers = headers.set("FBC", this._cookiesService.get("_fbc") || "");
    headers = headers.set("FBP", this._cookiesService.get("_fbp") || "");

    return this._httpClient.post<LegacyResponse<PSPPaymentResponse>>(
      `${this._purchasesApiUrl}/buy-upgrade`,
      data,
      { headers },
    );
  }

  public requestPayout(
    challengeId: string,
    method: PayoutMethod,
  ): Observable<LegacyResponse<Payout>> {
    return this._httpClient.post<LegacyResponse<Payout>>(`${this._payoutsApiUrl}/mine/request`, {
      payoutMethod: method,
      challengeId,
    });
  }

  public getUserProcessedPayouts(
    paginationPayload: PaginationPayload,
  ): Observable<ProcessedPayoutsPaginated> {
    const { page, size } = paginationPayload;

    return this._httpClient.get<ProcessedPayoutsPaginated>(
      `${this._payoutsApiUrl}/mine/processed`,
      { params: { page, size } },
    );
  }

  public getOrderHistory(paginationPayload: PaginationPayload): Observable<OrdersPaginated> {
    const { page, size } = paginationPayload;

    return this._httpClient.get<OrdersPaginated>(`${this._purchasesApiUrl}/orders`, {
      params: { page, size },
    });
  }

  public getAddOns(): Observable<ChallengeAddOn[]> {
    return this._httpClient.get<ChallengeAddOn[]>(`${this._purchasesApiUrl}/addons/mine`);
  }

  public downloadInvoice(orderId: string): Observable<Blob> {
    return this._httpClient.get(`${this._purchasesApiUrl}/orders/${orderId}/invoice`, {
      responseType: "blob",
    });
  }

  public downloadPNLCard(challengeId: string, tradeId: string): Observable<Blob> {
    return this._httpClient.get(
      `${this._challengesApiUrl}/${challengeId}/positions/${tradeId}/pnl-card`,
      { responseType: "blob" },
    );
  }

  public getContracts(paginationPayload: PaginationPayload): Observable<ContractsPaginated> {
    const { page, size } = paginationPayload;

    return this._httpClient.get<ContractsPaginated>(`${this._contractsApiUrl}/mine`, {
      params: { page, size },
    });
  }

  public getContractLink(phase2ChallengeId: string): Observable<string> {
    return this._httpClient.get(`${this._contractsApiUrl}/${phase2ChallengeId}/link`, {
      responseType: "text",
    });
  }

  public getAddonsUpgrade(challengeId: string): Observable<ChallengeAddOn[]> {
    return this._httpClient.get<ChallengeAddOn[]>(
      `${this._purchasesApiUrl}/${challengeId}/upgrade-addons`,
    );
  }

  public getTotalPayoutsNumber(): Observable<{ totalPayouts: number }> {
    return this._httpClient.get<{ totalPayouts: number }>(`${this._payoutsApiUrl}/total`);
  }
}
